/**
 * /organization — the organization editor, keyed to WORKSPACES
 * (multi-org plan D1/§6A, re-keyed 2026-08-16).
 *
 * Organization information lives HERE, personal information lives on
 * /profile. The page opens on the ACTIVE workspace's organization and the
 * switcher lists every workspace the server verified the user belongs to —
 * the same list the header pill shows, so the two never disagree. Everyone
 * in an org can always SEE its information; editing is gated by their
 * membership permission (edit_org_info — owners/admins by default), enforced
 * server-side. Org contact is organization-level (main phone/email/address)
 * — never a member's personal phone or email.
 *
 * §6A renames: the NAME never saves directly — renameWorkspaceOrganization
 * routes it through the server's governance ladder (free during trial and
 * for a never-subscribed solo native org; fresh sign-in + emailed code +
 * 24h cooldown + owner/admin notices for established orgs).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SeoHead from '../components/SeoHead';
import GlobalFooter from '../components/GlobalFooter';
import { ORG_SIZE_BANDS } from '../config/planRecommendation';
import {
  Organization as Org,
  OrgMembership,
  RenameOutcome,
  ensureOrgFactsForWorkspace,
  loadMyMembership,
  loadOrganization,
  orgIdForWorkspace,
  renameWorkspaceOrganization,
  saveOrganization,
} from '../services/organizations';
import { fetchWorkspaceOptionsCached, clearWorkspaceOptionsCache } from '../services/workspaceOptionsCache';
import { readConfirmedWorkspace } from '../services/prest0nRuntime/confirmedWorkspaceCache';
import { workspaceLabel } from '../services/workspaceIdentity';
import { toRosterRows, type RosterRow } from '../services/workspaceRoster';
import {
  listMembers as listWorkspaceRoster,
  type WorkspaceOption,
} from '../services/rbacService';
import { beginWorkspaceTransition, subscribeWorkspaceTransition } from '../services/workspaceTransition';
import { getAuth } from 'firebase/auth';

const PageContainer = styled.main`
  padding: 1.5rem 1rem;
  width: 100%;
  margin: 0 auto;
  min-height: 60vh;
  background: var(--brand-canvas);
  box-sizing: border-box;

  @media (min-width: 768px) { padding: 2rem 1.5rem; }
  @media (min-width: 1024px) { max-width: 900px; padding: 2.5rem; }
`;

const PageTitle = styled.h1`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(1.6rem, 4vw, 2.1rem);
  font-weight: 500;
  letter-spacing: -0.02em;
  text-transform: lowercase;
  color: var(--brand-text);
  margin-bottom: 1.5rem;
`;

const Section = styled.section`
  background: #fff;
  border: 1px solid #ECEAE4;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
`;

const SectionLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: lowercase;
  color: #98917F;
  margin-bottom: 1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const Field = styled.label`
  display: block;
  font-size: 0.8rem;
  color: #6F6A60;
  text-transform: lowercase;
`;

const Input = styled.input`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin-top: 0.35rem;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  color: var(--brand-text);
  background: #FCFBF7;
  border: 1px solid #E4E0D5;
  border-radius: 0.6rem;

  &:disabled { background: #F4F2EC; color: #7A746A; cursor: not-allowed; }
  &:focus { outline: none; border-color: var(--brand-gold); }
`;

const Select = styled.select`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin-top: 0.35rem;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  color: var(--brand-text);
  background: #FCFBF7;
  border: 1px solid #E4E0D5;
  border-radius: 0.6rem;

  &:disabled { background: #F4F2EC; color: #7A746A; cursor: not-allowed; }
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const SaveButton = styled.button`
  margin-top: 1.25rem;
  padding: 0.7rem 1.6rem;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: lowercase;
  color: #fff;
  background: linear-gradient(135deg, var(--brand-gold), #A8863C);
  border: none;
  border-radius: 0.7rem;
  cursor: pointer;

  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const Notice = styled.p`
  font-size: 0.84rem;
  color: #6F6A60;
  line-height: 1.6;
  margin: 0 0 1rem;
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: lowercase;
  color: #7A5C00;
  background: #F7F3E8;
  border: 1px solid rgba(198, 167, 94, 0.55);
  border-radius: 999px;
`;

const SwitcherRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const SavedFlash = styled.span`
  font-size: 0.82rem;
  color: #067647;
  margin-left: 0.75rem;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid #F0EDE5;
  flex-wrap: wrap;

  &:last-child { border-bottom: none; }
`;

const MemberName = styled.span`
  font-size: 0.9rem;
  color: var(--brand-text);
  min-width: 10rem;
  flex: 1;
`;

const RemoveButton = styled.button`
  padding: 0.3rem 0.7rem;
  font-size: 0.74rem;
  font-weight: 600;
  text-transform: lowercase;
  color: #9B2C2C;
  background: transparent;
  border: 1px solid rgba(153, 27, 27, 0.35);
  border-radius: 0.5rem;
  cursor: pointer;

  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

type FormState = Record<string, string>;

const IDENTITY_FIELDS: Array<{ key: keyof Org; label: string; placeholder?: string; full?: boolean }> = [
  { key: 'name', label: 'organization name', placeholder: 'Acme Inc.' },
  { key: 'industry', label: 'industry', placeholder: 'legal · accounting · logistics…' },
  { key: 'website', label: 'website', placeholder: 'https://…' },
  { key: 'focus', label: 'what the organization does', placeholder: 'one line on the work', full: true },
];

const CONTACT_FIELDS: Array<{ key: keyof Org; label: string; placeholder?: string; full?: boolean }> = [
  { key: 'main_phone', label: 'main phone (organization)', placeholder: 'front desk / main line' },
  { key: 'main_email', label: 'main email (organization)', placeholder: 'office@…' },
  { key: 'street_address', label: 'street address', full: true },
  { key: 'city', label: 'city' },
  { key: 'state_province', label: 'state / province' },
  { key: 'postal_code', label: 'postal code' },
  { key: 'country', label: 'country' },
];

const Organization: React.FC = () => {
  const [options, setOptions] = useState<WorkspaceOption[]>([]);
  const [viewedAccountId, setViewedAccountId] = useState<string | null>(null);
  const [org, setOrg] = useState<Org | null>(null);
  const [native, setNative] = useState(false);
  const [membership, setMembership] = useState<OrgMembership | null>(null);
  const [form, setForm] = useState<FormState>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Members roster (owner directive 2026-08-17: "the members section of the
  // organization page needs to be accurate and consistent with that of the
  // team & roles page … at all times"): rendered from the SAME server-resolved
  // authority Team & Roles uses (rbacListMembers, profile-joined names,
  // three-source owner rule) — never from the facts mirror, which drifts.
  // Role/permission MANAGEMENT lives in team & roles alone.
  const [roster, setRoster] = useState<RosterRow[]>([]);
  // §6A ceremony state: a pending emailed-code challenge, and human guidance.
  const [renameChallenge, setRenameChallenge] = useState<{ maskedEmail: string } | null>(null);
  const [renameCode, setRenameCode] = useState('');
  const [renameNotice, setRenameNotice] = useState<string | null>(null);
  const [switchingWorkspace, setSwitchingWorkspace] = useState(false);

  // Owner directive 2026-08-17 ("when the user changes what organization /
  // workspace ... it must reflect in the top right"): choosing a workspace
  // here is a REAL switch, not a page-local view. Same contract as the header
  // pill — now through the SEALED transition (2026-08-19): full-screen ≥3s
  // blocking overlay, server-verified switch beneath it, hard reload IN
  // PLACE so every portal surface re-keys together. The page deliberately
  // does NOT repaint the target org before the switch is official (the old
  // optimistic setViewedAccountId painted the target's facts/roster while
  // the callable was still in flight — the trust break the owner flagged);
  // the reloaded page opens on the new active workspace.
  const handleViewingChange = useCallback((nextAccountId: string) => {
    if (!nextAccountId || nextAccountId === viewedAccountId || switchingWorkspace) return;
    const target = options.find((o) => o.accountId === nextAccountId);
    if (!target) return;
    if (target.isActive) {
      // Already the active workspace — a page-local view change only.
      setViewedAccountId(nextAccountId);
      return;
    }
    setSwitchingWorkspace(true);
    // DORMANT rows ride the same sealed transition (2026-08-20 — this used
    // to bypass the overlay): the controller detects dormancy from the
    // roster, activates, and lands on /onboarding. Failure releases the
    // transition, which re-enables the selector below.
    beginWorkspaceTransition({
      targetAccountId: nextAccountId,
      targetLabel: workspaceLabel(target, getAuth().currentUser?.uid),
      uid: getAuth().currentUser?.uid || null,
      navigation: 'reload',
    });
  }, [viewedAccountId, switchingWorkspace, options]);

  // A failed sealed switch releases the overlay — re-enable the selector.
  useEffect(() => subscribeWorkspaceTransition((s) => {
    if (!s) setSwitchingWorkspace(false);
  }), []);

  // Boot: the server-verified workspace list; open on the ACTIVE workspace.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Pass the uid (2026-08-25 owner report: this page takes much too long
      // to load). Without it fetchWorkspaceOptionsCached skips its persisted
      // copy entirely and every visit waits out a full callable round trip —
      // cold starts included — before the page can even decide WHICH
      // workspace to open, which gates the whole hydrate below it. With the
      // uid the list is served from the last server-verified copy in the same
      // frame and refreshed in the background.
      const uid = getAuth().currentUser?.uid || '';
      const opts = await fetchWorkspaceOptionsCached(uid || undefined);
      if (cancelled) return;
      setOptions(opts);
      // ORG STICKINESS (INCIDENT-20260818): never GUESS the current org —
      // the old first-row fallback opened the alphabetically-first workspace when
      // no row carried isActive. Preference: last CONFIRMED workspace →
      // isActive → the uid (native org) only as the final fallback.
      // (PR #925 reconciliation: main's confirmed-workspace preference
      // supersedes this branch's parallel getLastWorkspace fallback.)
      const confirmed = readConfirmedWorkspace(uid);
      const initial =
        (confirmed ? opts.find((o) => o.accountId === confirmed.accountId) : undefined)
        || opts.find((o) => o.isActive)
        || null;
      setViewedAccountId(initial?.accountId || uid || null);
      if (!initial && !uid) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Viewed workspace: hydrate everything, with the bridge-ensure alongside
  // rather than in front of it.
  //
  // The ensure used to be a hard serial hop: nothing was read until the
  // callable came back, so the page cost two round trips before it could
  // paint anything (three, counting the workspace list above). But
  // ensureOrgFacts only CREATES the facts/membership docs for a member who
  // does not have them yet, and the orgId it returns is derived from the
  // accountId — `orgIdForWorkspace` is a pure `org_${accountId}`. So the
  // reads do not depend on it, and the common case (docs already exist) is
  // one parallel round instead of two serial ones.
  //
  // Correctness is preserved by re-reading in exactly the cases where the
  // speculative round could not have seen the truth: the ensure named a
  // DIFFERENT org, or it just created docs the first read found missing.
  // Both read helpers swallow their own errors and return null, so a
  // permission-denied read before the ensure lands is a null, never a throw.
  useEffect(() => {
    if (!viewedAccountId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const derivedOrgId = orgIdForWorkspace(viewedAccountId);
      const ensurePromise = ensureOrgFactsForWorkspace(viewedAccountId);
      const readAll = (orgId: string) => Promise.all([
        loadOrganization(orgId),
        loadMyMembership(orgId),
        listWorkspaceRoster(viewedAccountId).catch(() => null),
      ]);

      let [orgDoc, mem, rosterRes] = await readAll(derivedOrgId);
      const ensured = await ensurePromise;
      const orgId = ensured?.orgId || derivedOrgId;
      if (orgId !== derivedOrgId || (ensured !== null && (!orgDoc || !mem))) {
        [orgDoc, mem, rosterRes] = await readAll(orgId);
      }
      if (cancelled) return;
      setOrg(orgDoc);
      setNative(ensured?.native === true);
      setMembership(mem);
      setRoster(toRosterRows(rosterRes?.members));
      setRenameChallenge(null);
      setRenameCode('');
      setRenameNotice(null);
      const next: FormState = {};
      for (const f of [...IDENTITY_FIELDS, ...CONTACT_FIELDS]) {
        next[f.key as string] = (orgDoc?.[f.key] as string) || '';
      }
      next.size_band = orgDoc?.size_band || '';
      setForm(next);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [viewedAccountId]);

  const canEdit = membership?.permissions.edit_org_info === true;
  const myUid = getAuth().currentUser?.uid || '';
  // Managers = the org owner + role:admin members (mirrors the Firestore rule).
  const canManageMembers =
    membership?.role === 'owner' || membership?.role === 'admin';
  const active = org;
  const activeId = useMemo(
    () => (viewedAccountId ? orgIdForWorkspace(viewedAccountId) : null),
    [viewedAccountId],
  );

  // Voluntary exit (owner directive 2026-08-16): the server enforces
  // non-owner-only and converts the org's threads to the owners first.
  const [leaving, setLeaving] = useState(false);
  const handleLeave = useCallback(async () => {
    if (!viewedAccountId || leaving) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      'Leave this organization? Your threads here stay with the organization (its owners take '
      + 'them over with full history) and disappear from your view. Rejoining needs a new invitation.',
    )) return;
    setLeaving(true);
    try {
      const { leaveOrganization } = await import('../services/rbacService');
      await leaveOrganization(viewedAccountId);
      clearWorkspaceOptionsCache();
      window.location.reload();
    } catch (e: any) {
      setLeaving(false);
      setRenameNotice(String(e?.message || 'could not leave the organization').toLowerCase());
    }
  }, [viewedAccountId, leaving]);

  const set = useCallback((k: string, v: string) => {
    setForm((prev) => ({ ...prev, [k]: v }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!activeId || !viewedAccountId || !canEdit) return;
    setSaving(true);
    setRenameNotice(null);

    // Everything except the name saves directly (rules-gated by edit_org_info).
    const rest: Record<string, string> = { ...form };
    delete rest.name;
    const ok = await saveOrganization(activeId, rest as any);

    // The name goes through §6A governance whenever it actually changed.
    const requestedName = (form.name || '').trim();
    const currentName = (org?.name || '').trim();
    let renamed = false;
    if (requestedName && requestedName !== currentName) {
      const outcome: RenameOutcome = await renameWorkspaceOrganization(
        viewedAccountId,
        requestedName,
        renameChallenge ? renameCode.trim() || undefined : undefined,
      );
      if (outcome.status === 'renamed') {
        renamed = true;
        setRenameChallenge(null);
        setRenameCode('');
        setOrg((prev) => (prev ? { ...prev, name: requestedName } : prev));
        // The pill/switcher labels read the same identity — refresh them.
        clearWorkspaceOptionsCache();
        setOptions((prev) => prev.map((o) =>
          o.accountId === viewedAccountId ? { ...o, displayName: requestedName } : o));
      } else if (outcome.status === 'code-sent') {
        setRenameChallenge({ maskedEmail: outcome.maskedEmail });
        setRenameNotice(
          `to confirm the rename, enter the 6-digit code we just emailed to ${outcome.maskedEmail || 'your address'} and save again.`,
        );
      } else if (outcome.status === 'error') {
        setRenameNotice(
          outcome.reason === 'reauth-required'
            ? 'renaming an established organization needs a fresh sign-in — sign out, sign back in, then rename within 15 minutes.'
            : outcome.message.toLowerCase(),
        );
      }
    }

    setSaving(false);
    if (ok || renamed) setSavedAt(Date.now());
  }, [activeId, viewedAccountId, canEdit, form, org, renameChallenge, renameCode]);

  const renderField = (f: { key: keyof Org; label: string; placeholder?: string; full?: boolean }) => {
    const input = (
      <Field key={f.key as string}>
        {f.label}
        <Input
          type="text"
          value={form[f.key as string] || ''}
          placeholder={f.placeholder}
          disabled={!canEdit}
          onChange={(e) => set(f.key as string, e.target.value)}
        />
      </Field>
    );
    return f.full ? <FullWidth key={f.key as string}>{input}</FullWidth> : input;
  };

  return (
    <>
      <PageContainer>
        <SeoHead
          title="Organization | prest0"
          description="Your organization's information — identity, contact, and team."
          noIndex={true}
        />
        <PageTitle>organization</PageTitle>

        {/* Task #75 (owner directive 2026-08-18): start ANOTHER organization
            from here too — same staged onboarding, org exists only once that
            setup completes. Renders in the single-org state as well (the
            switcher row below hides at one option). */}
        <SwitcherRow style={{ justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => window.location.assign('/onboarding?neworg=1')}
            data-testid="organization-create-org"
            style={{
              background: 'transparent', border: '1px solid var(--color-accent, var(--brand-gold))',
              borderRadius: '999px', padding: '0.45rem 1rem', cursor: 'pointer',
              font: 'inherit', fontSize: '0.85rem', color: 'var(--color-text-primary, var(--brand-text))',
            }}
          >
            + start a new organization
          </button>
        </SwitcherRow>

        {options.length > 1 && (
          <SwitcherRow>
            <Field as="span">viewing</Field>
            <Select
              value={viewedAccountId || ''}
              disabled={switchingWorkspace}
              onChange={(e) => handleViewingChange(e.target.value)}
              aria-label="Switch organization"
              style={{ maxWidth: '20rem', marginTop: 0 }}
            >
              {options.map((o) => (
                <option key={o.accountId} value={o.accountId}>
                  {workspaceLabel(o, myUid)
                    + (o.isActive ? ' · active' : '')
                    + (o.dormant ? ' — not set up yet' : '')}
                </option>
              ))}
            </Select>
            {switchingWorkspace ? (
              <RoleBadge>switching workspace…</RoleBadge>
            ) : options.find((o) => o.accountId === viewedAccountId)?.isActive && (
              <RoleBadge>your active workspace</RoleBadge>
            )}
          </SwitcherRow>
        )}

        {loading ? (
          <Section><Notice>loading your organization…</Notice></Section>
        ) : !active ? (
          <Section>
            <SectionLabel>no organization yet</SectionLabel>
            <Notice>
              Your organization appears here once your account is set up — finish onboarding, or
              accept your team&rsquo;s invite, and this page fills itself in.
            </Notice>
          </Section>
        ) : (
          <>
            <Section>
              <SectionLabel>organization information</SectionLabel>
              {membership && (
                <Notice>
                  your role: <RoleBadge>{membership.role}</RoleBadge>
                  {!canEdit && (
                    <>
                      {' '}— you can view everything here; edits are managed by your
                      organization&rsquo;s admins.
                    </>
                  )}
                </Notice>
              )}
              <Grid>
                {IDENTITY_FIELDS.map(renderField)}
                <Field>
                  organization size
                  <Select
                    value={form.size_band || ''}
                    disabled={!canEdit}
                    onChange={(e) => set('size_band', e.target.value)}
                    aria-label="Organization size"
                  >
                    <option value="">how many people work there?</option>
                    {ORG_SIZE_BANDS.map((b: any) => (
                      <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                  </Select>
                </Field>
              </Grid>
              {/* §6A: the real-name reminder is for non-native orgs only —
                  the account-native organization NEVER sees it. */}
              {canEdit && !native && (
                <Notice style={{ marginTop: '0.9rem', marginBottom: 0 }}>
                  the organization name must be the organization&rsquo;s real name.
                </Notice>
              )}
              {renameNotice && (
                <Notice style={{ marginTop: '0.9rem', marginBottom: 0, color: '#7A5C00' }} data-rename-notice>
                  {renameNotice}
                </Notice>
              )}
              {renameChallenge && (
                <Field style={{ marginTop: '0.75rem', maxWidth: '14rem' }}>
                  rename confirmation code
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={renameCode}
                    placeholder="6-digit code"
                    onChange={(e) => setRenameCode(e.target.value)}
                    aria-label="Rename confirmation code"
                  />
                </Field>
              )}
            </Section>

            <Section>
              <SectionLabel>organization contact</SectionLabel>
              <Notice>
                How the organization is reached — a main line and office address, never anyone&rsquo;s
                personal phone or email.
              </Notice>
              <Grid>
                {CONTACT_FIELDS.map(renderField)}
              </Grid>
              {canEdit && (
                <>
                  <SaveButton type="button" onClick={handleSave} disabled={saving}>
                    {saving ? 'saving…' : 'save organization info'}
                  </SaveButton>
                  {savedAt && Date.now() - savedAt < 4000 && <SavedFlash>saved ✓</SavedFlash>}
                </>
              )}
            </Section>

            <Section>
              <SectionLabel>members</SectionLabel>
              <Notice>
                Everyone here can see the organization&rsquo;s information. This list always
                mirrors team &amp; roles — roles and permissions are managed there. Personal
                phone numbers and emails are never shown here.
              </Notice>
              {roster.length === 0 ? (
                <Notice>no members loaded.</Notice>
              ) : (
                roster.map((m) => (
                  <MemberRow key={m.uid} data-member-row>
                    <MemberName>
                      {m.name || 'unnamed member'}
                      {m.uid === myUid && ' (you)'}
                    </MemberName>
                    {/* Same truth Team & Roles renders: three-source owner rule +
                        primaryRoleId — never the facts mirror's frozen copy. */}
                    <RoleBadge>{m.roleLabel}</RoleBadge>
                    {m.paused && <RoleBadge>paused</RoleBadge>}
                  </MemberRow>
                ))
              )}
              {canManageMembers && (
                <Notice style={{ marginTop: '0.9rem', marginBottom: 0 }}>
                  invite teammates and manage roles in team &amp; roles. transferring ownership
                  is handled by prest0 support.
                </Notice>
              )}
              <Notice style={{ marginTop: '0.9rem', marginBottom: 0 }}>
                <a
                  href="/settings/team"
                  style={{ color: '#7A5C00', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                >
                  team &amp; roles →
                </a>
                {/* Voluntary exit (owner directive 2026-08-16): non-owners only. */}
                {membership && membership.role !== 'owner' && viewedAccountId && viewedAccountId !== myUid && (
                  <RemoveButton
                    type="button"
                    data-testid="org-page-leave-btn"
                    style={{ marginLeft: '1rem' }}
                    disabled={leaving}
                    onClick={handleLeave}
                  >
                    {leaving ? 'leaving…' : 'leave this organization…'}
                  </RemoveButton>
                )}
              </Notice>
            </Section>
          </>
        )}
      </PageContainer>
      <GlobalFooter />
    </>
  );
};

export default Organization;
